import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailWaterproofKit } from "../../../../../http/hrail/types/waterproofKits";
import { useHrailWaterproofKitsApi } from "../../../../../http/hrail/useWaterproofKits";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateWaterproofKitModalDialogButton from "./CreateWaterproofKitModalDialogButton";
import WaterproofKitForm from "./WaterproofKitForm";

type Props = {
  active?: boolean;
};

const WaterproofKits: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "waterproofKits" });
  const aborter = useRef(new AbortController());
  const waterproofKitApi = useHrailWaterproofKitsApi();
  const [itemToEdit, setItemToEdit] = useState<HrailWaterproofKit>();
  const [itemToDelete, setItemToDelete] = useState<HrailWaterproofKit>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "waterproofKits");
  const isActiveTab = active ?? true;
  const updateVisibility = async (waterproofKit: HrailWaterproofKit, visibility: VisibilityScopeValue) => {
    try {
      await waterproofKitApi.updateAdminHrailWaterproofKitVisibility(waterproofKit.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (waterproofKit: HrailWaterproofKit, newOrder: number) => {
    try {
      await waterproofKitApi.updateAdminHrailWaterproofKitOrder(waterproofKit.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.waterproof_kit");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = waterproofKitApi.useAdminHrailWaterproofKits(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.waterproofKits.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.waterproofKits.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateWaterproofKitModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailWaterproofKit>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="left">{i18n.translation.systems.plural}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(waterproofKit) => (
          <>
            <Table.Td noWrap={true}>{waterproofKit.item?.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{waterproofKit.item?.description ?? ""}</Table.Td>
            <td>{waterproofKit.systems?.map((system) => system.name).join("; ")}</td>
            <td align="center">
              <Visibility
                value={waterproofKit.visibility}
                onChange={(value) => updateVisibility(waterproofKit, value)}
              />
            </td>
            <td align="center">
              <InlineOrderEditor
                order={waterproofKit.order}
                onUpdate={(newOrder) => updateOrder(waterproofKit, newOrder)}
              />
            </td>
          </>
        )}
        onRowClick={(waterproofKit) => setItemToEdit(waterproofKit)}
        rowActions={(waterproofKit) => [
          getEditAction(i18n, () => setItemToEdit(waterproofKit)),
          accessesAction(waterproofKit.item),
          getDeleteAction(i18n, () => setItemToDelete(waterproofKit), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.waterproofKits.edit}>
          <WaterproofKitForm waterproofKit={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.waterproofKits.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                waterproofKitApi
                  .deleteAdminHrailWaterproofKit(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.waterproofKits.delete,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default WaterproofKits;
